import lodashGet from "lodash/get";
import React, { type ComponentPropsWithoutRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import * as XLSX from "xlsx";
import FileExportIcon from "shared/components/icons/file/FileExportIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { findPillarTableColumnComponent } from "shared/components/pillar-table/columns/utils/findPillarTableColumnComponent";
import {
  type CustomReportColumnConfig,
  renderCustomReportColumn,
} from "shared/components/pillar-table/custom-reports/customReportColumnsMapper";
import { isReachedLimitExcelRows } from "shared/components/pillar-table/export/isReachedLimitExcelRows";
import PillarTableExportToExcelColumn, {
  type PillarTableExportToExcelColumnProps,
} from "shared/components/pillar-table/export/PillarTableExportToExcelColumn";
import usePillarTableExportFileSizeValues from "shared/components/pillar-table/export/usePillarTableExportFileSizeValues";
import {
  isPillarTableNestedTable,
  type PillarTableNestedTableElement,
} from "shared/components/pillar-table/nested/pillarTableNestedTableHelpers";
import {
  PillarTableContext,
  usePillarTableContext,
  type DataQueryFunction,
  type PillarTableContextType,
} from "shared/components/pillar-table/PillarTableContext";
import { PillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import {
  CustomSessionContext,
  type CustomSessionType,
  useCustomSession,
} from "shared/context/CustomSessionContext";
import { type FiltersRequest } from "shared/filter-where-clause";
import { saveBinaryBufferToFile } from "shared/helpers/file/saveBinaryBufferToFile";

export type PillarTableExportToExcelProps<T> = Omit<
  ComponentPropsWithoutRef<"button">,
  "onClick"
> & {
  worksheetName: string;
  children:
    | Array<React.ReactElement<PillarTableExportToExcelColumnProps<T>>>
    | React.ReactElement<PillarTableExportToExcelColumnProps<T>>;
};

const PillarTableExportToExcel = <T,>({
  worksheetName,
  children,
  ...props
}: PillarTableExportToExcelProps<T>) => {
  const tableContext = usePillarTableContext<T, unknown>();
  const session = useCustomSession();
  const {
    dataQuery,
    tableRowResults,
    totalResults,
    filtersRequest,
    customReportResponse,
    testidTable,
  } = tableContext;

  if (customReportResponse?.exportColumns?.length) {
    children = buildExportColumnsFromCustomReport(
      customReportResponse.exportColumns,
    );
  }

  const { estimatedFileSizeKb } = usePillarTableExportFileSizeValues();

  const handleExport = async () => {
    if (!filtersRequest) {
      throw new Error("filters is required");
    }

    if (dataQuery instanceof Function) {
      await downloadThenProcessAndExportData<T>({
        dataQuery,
        filtersRequest,
        totalResults: totalResults!,
        estimatedFileSizeKb,
        children,
        worksheetName,
        tableContext,
        session,
      });
    } else {
      processAndExportData({
        combinedData: tableRowResults,
        children,
        worksheetName,
        tableContext,
        session,
      });
    }
  };
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return <></>;
  }

  return (
    <PillarForm.LoadingButton
      testid={testidTable ? `${testidTable}-export-to-excel-button` : undefined}
      onClick={() => handleExport()}
      title="Export"
      label="Export"
      className="w-12"
      icon={<FileExportIcon />}
      {...props}
    />
  );
};

export default PillarTableExportToExcel;

const buildExportColumnsFromCustomReport = (
  exportColumns?: CustomReportColumnConfig[],
): React.ReactElement[] => {
  if (!exportColumns?.length) {
    return [];
  }

  return exportColumns
    .map(
      (
        customReportColumnConfig: CustomReportColumnConfig,
        columnIndex: number,
      ) =>
        renderCustomReportColumn(
          customReportColumnConfig,
          `export-column-${columnIndex}`,
        ),
    )
    .filter(
      (column): column is React.ReactElement =>
        column !== null && React.isValidElement(column),
    );
};

const downloadThenProcessAndExportData = async <T,>({
  dataQuery,
  filtersRequest,
  totalResults,
  estimatedFileSizeKb,
  children,
  worksheetName,
  tableContext,
  session,
}: {
  dataQuery: DataQueryFunction<T, unknown>;
  filtersRequest: FiltersRequest;
  totalResults: number;
  estimatedFileSizeKb: number;
  children:
    | Array<React.ReactElement<PillarTableExportToExcelColumnProps<T>>>
    | React.ReactElement<PillarTableExportToExcelColumnProps<T>>;
  worksheetName: string;
  tableContext: PillarTableContextType<T, unknown>;
  session: CustomSessionType;
}) => {
  if (estimatedFileSizeKb <= 3500) {
    const data = await dataQuery(filtersRequest, {
      page: 1,
      pageSize: totalResults,
    });
    processAndExportData({
      combinedData: data.results,
      children,
      worksheetName,
      tableContext,
      session,
    });
  } else {
    const numberOfChunks = Math.ceil(estimatedFileSizeKb / 3500);
    const pageSize = Math.ceil(totalResults / numberOfChunks);

    // Create an array of page indexes [1, 2, 3, ..., numberOfChunks]
    const chunkIndexes = Array.from(
      { length: numberOfChunks },
      (_, i) => i + 1,
    );

    const allData: T[] = [];

    // Process up to 10 chunks at a time in parallel
    const concurrency = 10;
    for (let i = 0; i < chunkIndexes.length; i += concurrency) {
      // Slice out the next up-to-10 pages
      const slice = chunkIndexes.slice(i, i + concurrency);

      // Build an array of promises for this batch
      const dataPromises = slice.map((pageIndex) =>
        dataQuery(filtersRequest, { page: pageIndex, pageSize }),
      );

      // Wait for the batch to finish
      const batchResponses = await Promise.all(dataPromises);
      // Flatten the batch results into allData
      for (const response of batchResponses) {
        allData.push(...response.results);
      }
    }

    processAndExportData({
      combinedData: allData,
      children,
      worksheetName,
      tableContext,
      session,
    });
  }
};

// Helper function to render React elements with mock row context for export
const renderElementWithMockContext = <T,>({
  element,
  rowValue,
  tableContext,
  session,
}: {
  element: React.ReactElement;
  rowValue: T;
  tableContext: PillarTableContextType<T, unknown>;
  session: CustomSessionType;
}): string => {
  // Create a mock row context that satisfies the PillarTableRowContext requirements
  const mockRowContext = {
    rowValue,
    setRowValue: () => {
      // No-op for export
    },
    rowIndex: 0,
    rowExpanded: "none" as const,
    setRowExpanded: () => {
      // No-op for export
    },
    inlineEditMode: "none" as const,
    formValues: {},
    setFormValues: () => {
      // No-op for export
    },
    setInlineEditMode: () => {
      // No-op for export
    },
  };

  // Wrap the element with the row context
  const elementWithContext = (
    <CustomSessionContext.Provider value={session}>
      <PillarTableContext.Provider
        value={
          tableContext as PillarTableContextType<unknown, unknown, unknown>
        }
      >
        <PillarTableRowContext.Provider value={mockRowContext}>
          {element}
        </PillarTableRowContext.Provider>
      </PillarTableContext.Provider>
    </CustomSessionContext.Provider>
  );

  try {
    // Use renderToStaticMarkup to render the React element to HTML string
    const htmlString = renderToStaticMarkup(elementWithContext);
    //Create a document fragment to extract text content
    // This is necessary to handle cases where the element might be a complex React component
    const documentFragment = document.createElement("div");
    documentFragment.innerHTML = htmlString;

    // Extract text content from HTML by removing all HTML tags
    const textContent = documentFragment.innerText.trim();

    return `${textContent || ""}`;
  } catch (error) {
    // console.warn("Failed to render element for export:", error);
    return "";
  }
};

const processRows = <T,>({
  rows,
  columns,
  tableContext,
  session,
}: {
  rows: T[];
  columns: React.ReactElement[];
  tableContext: PillarTableContextType<T, unknown>;
  session: CustomSessionType;
}): unknown[][] => {
  const outputData: unknown[][] = [];

  rows.forEach((rowValue) => {
    const rowOutput: unknown[] = [];
    try {
      columns.forEach((child, childIndex) => {
        if (!React.isValidElement<unknown>(child)) {
          // Safely skip anything that isn't a valid React element
          // console.warn("Invalid child at index", childIndex, child);
          return;
        }

        const props = child.props as Record<string, unknown>;
        const actualColumnType = findPillarTableColumnComponent(child.type);
        //Legacy compatibility for ExportToExcelColumn even though it is deprecated
        if (
          !(
            (actualColumnType &&
              (!("doNotExport" in actualColumnType) ||
                !actualColumnType.doNotExport)) ||
            child.type === PillarTableExportToExcelColumn
          )
        ) {
          return;
        }

        if (
          actualColumnType &&
          "adminOnly" in actualColumnType &&
          actualColumnType.adminOnly
        ) {
          return;
        }

        let output: unknown;

        //TODO:  This mess is necessary for backwards compatibility with the old ExportToExcelColumn pattern
        // Handle the pattern similar to PillarTableCellContent
        if ("children" in props === false) {
          // No children prop - render the React element with mock context
          output = renderElementWithMockContext<T>({
            element: child,
            rowValue,
            tableContext,
            session,
          });
        } else if (typeof props.children === "function") {
          // console.log("Using function children for export", {
          //   rowValue,
          //   props,
          // });
          const cellValue =
            props.valueProperty || props.cellValuePath
              ? lodashGet(
                  rowValue,
                  (props.valueProperty ?? props.cellValuePath) as string,
                )
              : rowValue;
          // console.log("Derived cellValue", cellValue);
          // Children is a function - call it with the row value and create element
          const functionResult = (props.children as (item: T) => unknown)(
            cellValue,
          );
          // Create a React element with the function result and pass to renderElementWithMockContext
          const elementWithResult = React.createElement(
            "span",
            {},
            functionResult as React.ReactNode,
          );
          output = renderElementWithMockContext<T>({
            element: elementWithResult,
            rowValue,
            tableContext,
            session,
          });
        } else {
          output = renderElementWithMockContext<T>({
            element: child,
            rowValue,
            tableContext,
            session,
          });
        }

        // Ensure output is a string or number for Excel
        if (React.isValidElement(output)) {
          // If output is a React element, try to extract text content
          output = extractTextFromReactElement(output);
        } else if (typeof output === "string" || typeof output === "number") {
          //Don't do anything if it's already a string or number. Should only happen from Legacy ExportToExcelColumn functions
          // console.warn(
          //   "Output is already a string or number, no need to process further.",
          //   output,
          // );
        } else if (output === null || output === undefined) {
          output = "";
        }

        rowOutput.push(output);
      });
    } catch (e) {
      console.error(e);
    }
    outputData.push(rowOutput);
  });
  return outputData;
};

// Helper function to extract text content from React elements
const extractTextFromReactElement = (element: React.ReactElement): string => {
  if (typeof element === "string" || typeof element === "number") {
    return String(element);
  }

  if (React.isValidElement(element)) {
    const props = element.props as Record<string, unknown>;
    const children = props?.children;
    if (typeof children === "string" || typeof children === "number") {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children
        .map((child) =>
          React.isValidElement(child)
            ? extractTextFromReactElement(child)
            : String(child),
        )
        .join("");
    }
    if (React.isValidElement(children)) {
      return extractTextFromReactElement(children);
    }
  }

  return "";
};

const filterTable = (child: unknown) => {
  if (React.isValidElement(child)) {
    try {
      const actualColumnType = findPillarTableColumnComponent(child.type);
      const shouldExclude =
        actualColumnType &&
        "doNotExport" in actualColumnType &&
        actualColumnType.doNotExport;
      if (shouldExclude) {
        return false;
      }
      // If it's a PillarTableExportToExcelColumn, we want to include it even though it is deprecated for legacy reasons
      if (!actualColumnType && child.type !== PillarTableExportToExcelColumn) {
        return false;
      }

      return true;
    } catch (error) {
      // console.error("Error processing column type:", error, child);
      // If we can't determine the column type, include it by default
      return false;
    }
  }
  return true;
};

const retrieveHeaders = (child: unknown) => {
  if (React.isValidElement(child)) {
    const props = child.props as unknown as Record<string, unknown>;
    // Handle new PillarTable.Column pattern with heading
    if (props.heading) {
      return props.heading as string;
    }
    // Handle legacy ExportToExcelColumn pattern with header
    if (props.header) {
      return props.header as string;
    }
  }
  return "Unknown";
};

/** Excel rejects sheet names longer than 31 characters. */
const truncateWorksheetName = (worksheetName: string) =>
  worksheetName.length > 31 ? worksheetName.substring(0, 31) : worksheetName;

function addXLSXSpreadsheet({
  workBook,
  wsData,
  worksheetName,
}: {
  workBook: XLSX.WorkBook;
  wsData: unknown[][];
  worksheetName: string;
}) {
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  XLSX.utils.book_append_sheet(
    workBook,
    ws,
    truncateWorksheetName(worksheetName),
  );
}

/**
 * The columns one nested table exports: its custom report config when it came
 * from the report builder, otherwise the columns written inside its JSX.
 */
const resolveNestedTableColumns = ({
  nestedTable,
  adminMode,
}: {
  nestedTable: PillarTableNestedTableElement;
  adminMode?: boolean;
}): React.ReactElement[] => {
  const { nestedTableColumns, children } = nestedTable.props as {
    nestedTableColumns?: CustomReportColumnConfig[];
    children?: React.ReactNode;
  };

  if (nestedTableColumns?.length) {
    return nestedTableColumns
      .filter((columnConfig) => adminMode || !columnConfig.adminOnly)
      .map((columnConfig, columnIndex) =>
        renderCustomReportColumn(
          columnConfig,
          `nested-export-column-${columnIndex}`,
        ),
      )
      .filter((column): column is React.ReactElement => column !== null);
  }

  return React.Children.toArray(children)
    .filter((child): child is React.ReactElement => React.isValidElement(child))
    .filter(filterTable);
};

/**
 * Sheet names for the nested tables, unique within the workbook. A nested
 * table's `heading` names its sheet when it has one, because "Nested
 * information 3" tells the reader nothing once a row has several relations.
 */
const buildNestedWorksheetNames = ({
  nestedTables,
  worksheetName,
}: {
  nestedTables: PillarTableNestedTableElement[];
  worksheetName: string;
}): string[] => {
  const usedNames = new Set<string>([truncateWorksheetName(worksheetName)]);

  return nestedTables.map((nestedTable, nestedTableIndex) => {
    const baseName =
      (nestedTable.props as { heading?: string }).heading ||
      (nestedTableIndex === 0
        ? "Nested information"
        : `Nested information ${nestedTableIndex + 1}`);

    let uniqueName = truncateWorksheetName(baseName);
    let duplicateCount = 2;
    while (usedNames.has(uniqueName)) {
      uniqueName = truncateWorksheetName(`${baseName} ${duplicateCount}`);
      duplicateCount++;
    }
    usedNames.add(uniqueName);
    return uniqueName;
  });
};

const saveCSVFile = (wsData: unknown[][], worksheetName: string) => {
  const csvData = wsData
    .map((row) =>
      row
        .map((col) => {
          if (typeof col === "string" && /[",\n\r]/.test(col)) {
            // eslint-disable-next-line quotes
            return `"${col.replace(/"/g, '""')}"`;
          }
          return col;
        })
        .join(","),
    )
    .join("\n");
  const uintArray = new TextEncoder().encode(csvData);
  const csvFileType = "text/csv";
  saveBinaryBufferToFile(uintArray, `${worksheetName}.csv`, csvFileType);
};

const processAndExportData = <T,>({
  combinedData,
  children,
  worksheetName,
  tableContext,
  session,
}: {
  combinedData: T[];
  children:
    | Array<React.ReactElement<PillarTableExportToExcelColumnProps<T>>>
    | React.ReactElement<PillarTableExportToExcelColumnProps<T>>;
  worksheetName: string;
  tableContext: PillarTableContextType<T, unknown>;
  session: CustomSessionType;
}) => {
  // Extract headers from children, handling both new and legacy patterns
  const childArray = React.Children.toArray(children);

  const mainTable = childArray.filter(
    (el): el is React.ReactElement =>
      React.isValidElement(el) && !isPillarTableNestedTable(el),
  );
  const filteredMainTable = mainTable.filter(filterTable);

  const mainTableHeaders = filteredMainTable.map(retrieveHeaders);
  const mainTableData = processRows<T>({
    rows: combinedData,
    columns: filteredMainTable,
    tableContext: tableContext,
    session,
  });
  const mainTableWsData = [mainTableHeaders, ...mainTableData];

  const nestedTables = childArray.filter(isPillarTableNestedTable);
  const nestedWorksheetNames = buildNestedWorksheetNames({
    nestedTables,
    worksheetName,
  });

  const processedNestedTablesData = nestedTables.map(
    (nestedTable, nestedTableIndex) => {
      const nestedTableColumns = resolveNestedTableColumns({
        nestedTable,
        adminMode: session?.adminMode,
      });

      // Every parent column, not just the first one: a nested row on its own
      // sheet is unreadable without the whole parent row it belongs to.
      const nestedTableHeaders = [
        ...mainTableHeaders,
        ...nestedTableColumns.map(retrieveHeaders),
      ];

      const nestedTablePath = nestedTable.props.valuePath;

      const linkedNestedRows = combinedData.flatMap((row, rowIndex) => {
        const rowNestedItems = lodashGet(row, nestedTablePath) || [];
        const parentRow = mainTableData[rowIndex] ?? [];
        return rowNestedItems.map((nestedRow: T) => ({
          parentRow,
          data: nestedRow,
        }));
      });

      const nestedDataOnly = linkedNestedRows.map((row) => row.data);
      const processedNestedData = processRows({
        rows: nestedDataOnly,
        columns: nestedTableColumns,
        tableContext,
        session,
      });

      const nestedTableData = processedNestedData.map((row, index) => [
        ...(linkedNestedRows[index]?.parentRow ?? []),
        ...row,
      ]);

      return {
        nestedTableData,
        nestedTableWsData: [nestedTableHeaders, ...nestedTableData],
        nestedWorksheetName: nestedWorksheetNames[nestedTableIndex],
      };
    },
  );

  //if mainTableWsData is more than 34,000 rows we want a csv file otherwise we want an xlsx file
  if (!isReachedLimitExcelRows(mainTableWsData.length)) {
    const wb = XLSX.utils.book_new();
    addXLSXSpreadsheet({
      workBook: wb,
      wsData: mainTableWsData,
      worksheetName: `${worksheetName}`,
    });
    processedNestedTablesData.forEach(
      ({ nestedTableData, nestedTableWsData, nestedWorksheetName }) => {
        if (nestedTableData.length) {
          addXLSXSpreadsheet({
            workBook: wb,
            wsData: nestedTableWsData,
            worksheetName: nestedWorksheetName,
          });
        }
      },
    );

    const xlsxFileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const file = XLSX.write(wb, { type: "buffer", bookType: "xlsx" });
    saveBinaryBufferToFile(file, `${worksheetName}.xlsx`, xlsxFileType);
    return;
  }

  saveCSVFile(mainTableWsData, worksheetName);
  processedNestedTablesData.forEach(
    ({ nestedTableData, nestedTableWsData, nestedWorksheetName }) => {
      if (nestedTableData.length) {
        saveCSVFile(
          nestedTableWsData,
          `${worksheetName} - ${nestedWorksheetName}`,
        );
      }
    },
  );
};
